import { instance } from './instance';

export interface StaffFeedPost {
  id: string;
  author: { id: number; firstName: string };
  caption: string;
  audience: 'PUBLIC' | 'PRIVATE';
  createdAt: string;
  removed?: boolean;
  meals: string[];
  restaurants: string[];
  ratings: { rating: number; brand: { name: string } }[];
  media: {
    id: string;
    kind: 'PHOTO' | 'VIDEO';
    url: string;
    thumbnailUrl: string;
  }[];
}

export interface FeedReport {
  id: string;
  postId: string;
  commentId: string | null;
  reason: string;
  createdAt: string;
  resolvedAt: string | null;
  comment: { body: string; author: { firstName: string } } | null;
}

export interface FeedApproval {
  id: string;
  kind: 'POST' | 'COMMENT';
  postId: string;
  author: {
    id: number;
    firstName: string;
    lastName?: string | null;
    username?: string | null;
  };
  body: string;
  audience: 'PUBLIC' | 'PRIVATE';
  version: number;
  status: FeedModerationStatus;
  source: FeedModerationSource;
  reason: string | null;
  reviewDecision: 'APPROVE' | 'REJECT' | null;
  reviewNote: string | null;
  negativeProbability: number | null;
  profanityProbability: number | null;
  model: string | null;
  createdAt: string;
  activityAt: string;
  queuedAt: string | null;
  reviewedAt: string | null;
}

export type FeedModerationStatus = 'ALL' | 'APPROVED' | 'PENDING' | 'REJECTED';
export type FeedModerationSource =
  | 'ALL'
  | 'BETA_IMPORT'
  | 'REVIEW_IMPORT'
  | 'USER_CREATED';
export type FeedModerationKind = 'ALL' | 'POST' | 'COMMENT';

export interface FeedModerationFilters {
  status: FeedModerationStatus;
  source: FeedModerationSource;
  kind: FeedModerationKind;
  author?: string;
}

export const staffFeed = {
  pending: async (
    cursor?: string
  ): Promise<{ items: FeedApproval[]; nextCursor: string | null }> =>
    (await instance.get('/admin/feed/pending', { params: { cursor } })).data
      .data,
  moderation: async (
    filters: FeedModerationFilters,
    cursor?: string
  ): Promise<{ items: FeedApproval[]; nextCursor: string | null }> =>
    (
      await instance.get('/admin/feed/moderation', {
        params: {
          ...filters,
          author: filters.author?.trim() || undefined,
          cursor,
        },
      })
    ).data.data,
  review: (
    item: FeedApproval,
    decision: 'APPROVE' | 'REJECT',
    reason?: string
  ) =>
    instance.patch(
      `/admin/feed/${item.kind === 'POST' ? 'posts' : 'comments'}/${
        item.id
      }/review`,
      {
        decision,
        version: item.version,
        ...(reason?.trim() ? { reason: reason.trim() } : {}),
      }
    ),
  reports: async (
    resolved: boolean,
    cursor?: string
  ): Promise<{ reports: FeedReport[]; nextCursor: string | null }> =>
    (
      await instance.get('/admin/feed/reports', {
        params: { resolved, cursor },
      })
    ).data.data,
  post: async (id: string): Promise<StaffFeedPost> =>
    (await instance.get(`/admin/feed/posts/${id}`)).data.data,
  remove: (report: FeedReport, reason: string) =>
    instance.delete(
      report.commentId
        ? `/admin/feed/posts/${report.postId}/comments/${report.commentId}`
        : `/admin/feed/posts/${report.postId}`,
      { data: { reason } }
    ),
  resolve: (id: string) => instance.patch(`/admin/feed/reports/${id}/resolve`),
};
